<template>
  <div ref="chart">
    <Chart :height="height" :id="id" :option="option" :width="width"></Chart>
  </div>
</template>

<script>
import Chart from '@/components/echarts/Chart';

export default {
  name: "UPSBarChart",
  components: { Chart },
  props: {
    data: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      id: "UPSBarChart_" + Math.random().toString(36).substr(2, 9),
      width: '100%',
      height: '100%',
      option: {}
    };
  },
  watch: {
    data: {
      handler() {
        this.getOption();
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.getOption();
  },
  methods: {
    getOption() {
      if (!this.data) return;
      
      const { deviceNames, statuses, aInputVoltages, bInputVoltages, cInputVoltages, thresholds, pointNames } = this.data;
      
      const names = pointNames || ['A相输入电压', 'B相输入电压', 'C相输入电压'];

      // 构建阈值线生成函数
      const createMarkLineData = (thresholdList, phaseColor) => {
        const markLineData = [];
        if (thresholdList && Array.isArray(thresholdList)) {
          thresholdList.forEach(t => {
            const namePrefix = t.pointName || '';
            const level = t.levelName || '';
            if (t.alarmHigh != null) {
              markLineData.push({
                yAxis: t.alarmHigh,
                name: `${namePrefix}${level}上限`,
                lineStyle: { color: t.levelColor || phaseColor || '#ff0000', type: 'dashed', width: 2 },
                label: { 
                  show: true, 
                  position: 'end',
                  // formatter: `${namePrefix}${level}上限: {c}V`,
                  formatter: `{c}V`,
                  color: t.levelColor || phaseColor || '#ff0000',
                  fontSize: 11,
                  // backgroundColor: 'rgba(0,0,0,0.6)',
                  padding: [2, 4],
                  borderRadius: 2
                }
              });
            }
            if (t.alarmLow != null) {
              markLineData.push({
                yAxis: t.alarmLow,
                name: `${namePrefix}${level}下限`,
                lineStyle: { color: t.levelColor || phaseColor || '#ff0000', type: 'dashed', width: 2 },
                label: { 
                  show: true, 
                  position: 'end',
                  // formatter: `${namePrefix}${level}下限: {c}V`,
                  formatter: `{c}V`,
                  color: t.levelColor || phaseColor || '#ff0000',
                  fontSize: 11,
                  // backgroundColor: 'rgba(0,0,0,0.6)',
                  padding: [2, 4],
                  borderRadius: 2
                }
              });
            }
          });
        }
        return markLineData;
      };

      // 生成各相位的阈值线，使用各相位对应的颜色
      const markLineDataA = thresholds ? createMarkLineData(thresholds.stageA, '#FACC15') : [];
      const markLineDataB = thresholds ? createMarkLineData(thresholds.stageB, '#22C55E') : [];
      const markLineDataC = thresholds ? createMarkLineData(thresholds.stageC, '#EF4444') : [];
      
      this.option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>';
            params.forEach(item => {
              if (item.componentType === 'series') {
                result += item.marker + item.seriesName + ': ' + item.value + 'V<br/>';
              }
            });
            return result;
          }
        },
        legend: {
          data: names,
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          top: 8,
          left: 'center',
          itemWidth: 14,
          itemHeight: 10
        },
        grid: {
          left: '5%',
          // right: '45%',
          bottom: '10%',
          top: '22%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: deviceNames,
          axisLine: {
            lineStyle: {
              color: 'rgba(73, 164, 243, .4)'
            }
          },
          axisLabel: {
            color: (value, index) => {
              const status = statuses && statuses[index];
              // 0:通讯正常，1:通讯异常，-1:未连接，2:有告警
              if (status !== undefined && status !== 0) {
                return '#ff0000';
              }
              return '#fff';
            },
            fontSize: 10,
            interval: 0,
            rotate: 0,
            formatter: function(value) {
              if (value.length > 10) {
                return value.substring(0, 10) + '...';
              }
              return value;
            }
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: '电压(V)',
          nameTextStyle: {
            color: '#fff',
            fontSize: 10
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: 'rgba(73, 164, 243, .4)'
            }
          },
          axisLabel: {
            color: '#fff',
            fontSize: 10
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: 'rgba(73, 164, 243, .2)'
            }
          }
        },
        series: [
          {
            name: names[0],
            type: 'bar',
            barWidth: '18%',
            barGap: '30%',
            data: aInputVoltages,
            itemStyle: {
              color: '#FACC15',
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: false
            },
            markLine: markLineDataA.length > 0 ? {
              silent: true,
              symbol: 'none',
              data: markLineDataA
            } : null
          },
          {
            name: names[1],
            type: 'bar',
            barWidth: '18%',
            data: bInputVoltages,
            itemStyle: {
              color: '#22C55E',
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: false
            },
            markLine: markLineDataB.length > 0 ? {
              silent: true,
              symbol: 'none',
              data: markLineDataB
            } : null
          },
          {
            name: names[2],
            type: 'bar',
            barWidth: '18%',
            data: cInputVoltages,
            itemStyle: {
              color: '#EF4444',
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: false
            },
            markLine: markLineDataC.length > 0 ? {
              silent: true,
              symbol: 'none',
              data: markLineDataC
            } : null
          }
        ]
      };
    }
  }
}
</script>

<style scoped>
</style>

